import classNames from 'classnames';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Icon } from './Icon';
import { InputGroup } from './TextInput';

export interface SelectInputProps {
  name: string;
  value?: SelectInputOption | null;
  options: SelectInputOption[];
  recommendedOption?: SelectInputOption | null;
  placeholder?: string;
  state?: string;
  label?: string;
  leftIcon?: string;
  onChange?: (option: SelectInputOption) => void;
  searchable?: boolean;
}

export interface SelectInputOption {
  label: string;
  value: string | number | boolean;
  selected?: boolean;
}

interface OptionsDropdownProps {
  options: SelectInputOption[];
  recommendedOption?: SelectInputOption | null;
  value: SelectInputOption | null;
  show: boolean;
  onSelect: (option: SelectInputOption) => void;
  searchable?: boolean;
  searchText: string;
  setSearchText: (t: string) => void;
}

const SelectInput: FC<SelectInputProps> = (props) => {
  const [optionsVisible, setOptionsVisible] = useState(false);
  const [searchText, setSearchText] = useState<string>('');
  const ref = useRef<HTMLDivElement>(null);

  const state = props.state;
  const inputGroupProps = {
    ...props,
    state,
    type: 'select',
    rightIcon: 'chevron-down',
  };

  useEffect(() => {
    // https://stackoverflow.com/a/42234988
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOptionsVisible(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const Placeholder = () => <span className="oejr-placeholder">{props.placeholder}</span>;

  return (
    <InputGroup {...inputGroupProps}>
      <div className="oejr-select-options-wrapper" ref={ref}>
        <div className="oejr-input" onClick={() => setOptionsVisible(!optionsVisible)}>
          {props.value?.label || <Placeholder />}
        </div>
        <OptionsDropdown
          options={props.options}
          value={props.value || null}
          show={optionsVisible}
          onSelect={(option) => {
            setOptionsVisible(false);
            props.onChange && props.onChange(option);
            setSearchText('');
          }}
          searchable={props.searchable}
          searchText={searchText}
          setSearchText={setSearchText}
          recommendedOption={props.recommendedOption}
        />
      </div>
    </InputGroup>
  );
};

const OptionsDropdown: FC<OptionsDropdownProps> = ({
  options,
  recommendedOption,
  show,
  searchable,
  value,
  onSelect,
  searchText,
  setSearchText,
}) => {
  const __ = useTranslation().t;

  if (!show) {
    return null;
  }

  const filteredOptions = !searchText
    ? options
    : options.filter((o) => {
        return (
          (o.value + '').toLowerCase().includes(searchText.toLowerCase()) ||
          o.label.toLowerCase().includes(searchText.toLowerCase())
        );
      });

  return (
    <div className="oejr-select-options">
      {!!searchable && (
        <SearchInputWrapper>
          <StyledSearchInput
            placeholder={`${__('search')}...`}
            value={searchText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
          />
          <ClearInputButton onClick={() => setSearchText('')}>
            <Icon name="times" />
          </ClearInputButton>
        </SearchInputWrapper>
      )}
      {!!recommendedOption && !searchText && (
        <>
          <RecommendedOptionLabel>{__('recommendedOption')}</RecommendedOptionLabel>
          <Option
            key={-1}
            option={{ ...recommendedOption, selected: value?.value === recommendedOption.value }}
            onSelect={onSelect}
          />
          <RecommendedOptionLabel>{__('otherOptions')}</RecommendedOptionLabel>
        </>
      )}
      {filteredOptions.map((option, key) => {
        return (
          <Option
            key={key}
            option={{ ...option, selected: value?.value === option.value }}
            onSelect={onSelect}
          />
        );
      })}
    </div>
  );
};

const Option = (props: {
  option: SelectInputOption;
  onSelect: (option: SelectInputOption) => void;
}) => {
  const SelectedIcon = () => (props.option.selected ? <Icon name="check" /> : null);
  return (
    <div
      className={classNames('oejr-select-option', props.option.selected && 'oejr-selected-option')}
      onClick={() => props.onSelect(props.option)}
    >
      <span>{props.option.label}</span>
      <SelectedIcon />
    </div>
  );
};

export default SelectInput;

const StyledSearchInput = styled.input`
  border: 0 !important;
  border-radius: 0 !important;
  padding: 12px 0 !important;
  font-weight: normal !important;
  flex: 1;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  border-bottom: 1px solid #999 !important;
  margin: 0 16px;
`;

const ClearInputButton = styled.div`
  cursor: pointer;
  padding: 10px 10px 10px 0;
`;

const RecommendedOptionLabel = styled.div`
  margin: 0 16px;
  color: ${({ theme }) => theme.colors.medium};
  padding-top: 8px;
`;
